// A plan swap Chargebee holds as a ramp, as returned by GET /subscriptions/find-one
// (`scheduledPlanChange` / `lastPlanChange`). `toPlanPrice` is in dollars.
export type PlanChange = {
  rampId: string;
  fromItemPriceId: string | null;
  toItemPriceId: string;
  toPlanName: string | null;
  toPlanPrice: number | null;
  effectiveAt: string;
};

// The member removed their scheduled cancellation in this browser session.
// `renewsOn` is the renewal date shown in the green notice, when known.
export type Reactivation = { renewsOn: string | null };

export type SubscriptionNotice =
  | { kind: 'cancellation'; date: string }
  | { kind: 'reactivated'; renewsOn: string | null }
  | { kind: 'scheduledPlanChange'; change: PlanChange }
  | { kind: 'planChanged'; change: PlanChange };

const DISMISSED_KEY_PREFIX = 'planChangedNoticeDismissed:';
const REACTIVATION_KEY = 'membershipReactivated';
export const REACTIVATION_EVENT = 'afy:membership-reactivated';

export const formatNoticeDate = (date: string): string =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

export const planLabel = (change: PlanChange): string => {
  const name = change.toPlanName || 'your new plan';
  return change.toPlanPrice ? `${name} ($${change.toPlanPrice}/month)` : name;
};

// localStorage can throw (private mode, blocked storage); a failed read means
// "not dismissed" and a failed write only means the notice comes back.
export const isPlanChangeDismissed = (rampId: string): boolean => {
  try {
    return localStorage.getItem(`${DISMISSED_KEY_PREFIX}${rampId}`) === '1';
  } catch {
    return false;
  }
};

export const dismissPlanChange = (rampId: string): void => {
  try {
    localStorage.setItem(`${DISMISSED_KEY_PREFIX}${rampId}`, '1');
  } catch {
    // ignore
  }
};

// The reactivation lives in sessionStorage so the green notice follows the
// member across pages until they dismiss it or close the tab. Storage can
// throw (private mode, blocked storage); then the notice lasts for this page
// only. REACTIVATION_EVENT tells mounted listeners about a change.
export const readReactivation = (): Reactivation | null => {
  try {
    const raw = sessionStorage.getItem(REACTIVATION_KEY);
    return raw ? (JSON.parse(raw) as Reactivation) : null;
  } catch {
    return null;
  }
};

const announceReactivation = (reactivation: Reactivation | null): void => {
  window.dispatchEvent(new CustomEvent(REACTIVATION_EVENT, { detail: reactivation }));
};

export const markReactivated = (renewsOn: string | null): void => {
  const reactivation = { renewsOn };
  try {
    sessionStorage.setItem(REACTIVATION_KEY, JSON.stringify(reactivation));
  } catch {
    // ignore
  }
  announceReactivation(reactivation);
};

export const clearReactivation = (): void => {
  try {
    sessionStorage.removeItem(REACTIVATION_KEY);
  } catch {
    // ignore
  }
  announceReactivation(null);
};

/**
 * The one subscription notice the dashboard shows, most urgent first: a
 * scheduled cancellation, then a membership the member just kept, then a
 * scheduled plan change (Plans & billing only), then a plan change that
 * already happened and the member has not dismissed.
 */
export const selectSubscriptionNotice = ({
  isScheduledForCancellation,
  scheduledCancelDate,
  scheduledPlanChange,
  lastPlanChange,
  isDismissed,
  isOnPlansAndBilling,
  reactivation = null,
}: {
  isScheduledForCancellation: boolean;
  scheduledCancelDate: string | null;
  scheduledPlanChange: PlanChange | null;
  lastPlanChange: PlanChange | null;
  isDismissed: (rampId: string) => boolean;
  isOnPlansAndBilling: boolean;
  reactivation?: Reactivation | null;
}): SubscriptionNotice | null => {
  if (isScheduledForCancellation && scheduledCancelDate) {
    return { kind: 'cancellation', date: scheduledCancelDate };
  }
  if (reactivation) {
    return { kind: 'reactivated', renewsOn: reactivation.renewsOn };
  }
  if (scheduledPlanChange && isOnPlansAndBilling) {
    return { kind: 'scheduledPlanChange', change: scheduledPlanChange };
  }
  if (lastPlanChange && !isDismissed(lastPlanChange.rampId)) {
    return { kind: 'planChanged', change: lastPlanChange };
  }
  return null;
};
